/**
 * NOLAS 2026, © Mika (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/components/admin/ImpersonationBanner.tsx
 *
 * Rôle :
 *   Bandeau PERMANENT et NON MASQUABLE affiché tant qu'une usurpation
 *   « Voir en tant que » est en cours. Il dit trois choses : qui est
 *   incarné, que tout est en lecture seule, et combien de temps il reste.
 *   Il porte aussi le seul bouton de sortie.
 *
 *   Pourquoi non masquable : le vrai danger de cette fonction n'est pas
 *   technique (le serveur refuse déjà toute écriture), il est humain
 *   croire qu'on est sur son propre compte. Le bandeau ne se ferme donc
 *   pas, et il reste collé en haut de l'écran.
 *
 * Réutilisable :
 *   100 %. Ne connaît ni les rôles ni le métier : il affiche le `label`
 *   et le `role` que le serveur lui donne.
 *
 * Dépendances clés :
 *   - api/impersonation (contrat validé par Zod)
 *   - react-query       (état partagé avec le bouton de la liste des comptes)
 *
 * @author Mika
 * @since  2026-08-25
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  IMPERSONATION_QUERY_KEY,
  fetchImpersonationStatus,
  impersonationLandingPath,
  stopImpersonation,
} from '@/api/impersonation';
import { apiRequest } from '@/api/client';

/**
 * Bloc `impersonation` de `me/role.php` (ajouté le 2026-08-26). C'est LUI qui
 * rend le bandeau utilisable hors du back-office : la requête `status` exige
 * une session admin et répondrait 401 sur l'application publique.
 */
interface MeRoleImpersonation {
  readonly impersonation: {
    readonly active: boolean;
    readonly target_role: string;
    readonly target_label: string;
    readonly target_is_admin: boolean;
    readonly expires_in_seconds: number;
  } | null;
}
import styles from './ImpersonationBanner.module.css';

/** Formate un reste de temps en « 42 min » / « 3 min » / « moins d'1 min ». */
function formatRemaining(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return 'moins d’1 min';
  return `${minutes} min`;
}

export function ImpersonationBanner() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  // `me/role.php` est déjà interrogé par l'application (guards, coquille
  // admin) : on réutilise la même clé, donc AUCUNE requête de plus dans le
  // cas courant, et rien du tout pour un visiteur anonyme.
  const roleQuery = useQuery({
    queryKey: ['me', 'role'] as const,
    queryFn: async () => apiRequest<MeRoleImpersonation>('me/role.php'),
    staleTime: 30_000,
    retry: false,
  });
  const emprunt = roleQuery.data?.impersonation ?? null;
  const actif = emprunt?.active === true;

  // Le détail (dont `read_only`) ne s'obtient qu'avec une session admin :
  // on ne le demande donc QUE pendant un emprunt, sinon l'application
  // publique récolterait un 401 toutes les minutes.
  const query = useQuery({
    queryKey: IMPERSONATION_QUERY_KEY,
    queryFn: fetchImpersonationStatus,
    enabled: actif,
    // L'état EXPIRE tout seul au bout d'une heure : on resonde régulièrement
    // pour que le bandeau disparaisse de lui-même.
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  // Décompte local, pour ne pas interroger le serveur chaque seconde.
  // Il se resynchronise à chaque réponse de `status` (toutes les minutes).
  const serverRemaining = query.data?.expires_in_seconds
    ?? emprunt?.expires_in_seconds ?? null;
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    setRemaining(serverRemaining);
  }, [serverRemaining]);

  // L'intervalle ne dépend QUE du fait de décompter ou non : le pas se fait
  // par mise à jour fonctionnelle, donc sans redémarrer le minuteur.
  const isCounting = serverRemaining !== null;
  useEffect(() => {
    if (!isCounting) return undefined;
    const id = window.setInterval(() => {
      setRemaining((r) => (r === null ? null : Math.max(0, r - 30)));
    }, 30_000);
    return () => window.clearInterval(id);
  }, [isCounting]);

  const stop = useMutation({
    mutationFn: stopImpersonation,
    onSuccess: () => {
      // On revient à son propre compte : TOUT le cache doit repartir de zéro,
      // sinon les écrans continueraient d'afficher les données de la cible.
      // Même famille que le bug de boucle de connexion du 23/08.
      queryClient.clear();
      // Rechargement franc : le plus sûr moyen de repartir sur une identité
      // propre, sans dépendre de ce que chaque écran aurait mis en cache.
      window.location.assign('/admin/dashboard');
    },
  });

  const onStop = useCallback(() => {
    if (!stop.isPending) stop.mutate();
  }, [stop]);

  const label = useMemo(
    () => query.data?.target?.label ?? emprunt?.target_label ?? '',
    [query.data, emprunt],
  );
  const role = useMemo(
    () => query.data?.target?.role ?? emprunt?.target_role ?? '',
    [query.data, emprunt],
  );

  // On s'appuie sur `me/role.php` et NON sur `status` : le bandeau doit
  // s'afficher dans l'application publique, où `status` répond 401.
  if (!actif || emprunt === null) {
    return null;
  }

  const remainingLabel = formatRemaining(remaining);

  return (
    <div className={styles.banner} role="alert" aria-live="assertive">
      <span className={styles.icon} aria-hidden="true">
        👁
      </span>
      <p className={styles.text}>
        <FormattedMessage
          id="impersonation.banner.text"
          values={{
            who: <span className={styles.who}>{label}</span>,
          }}
        />
        <span className={styles.role}>{role}</span>
        <span className={styles.readonly}>
          {', '}
          <FormattedMessage
            // Le detail n'est pas encore charge ? On annonce « tracé », qui
            // est l'etat reel depuis le 26/08. Se tromper dans CE sens rend
            // prudent ; annoncer « lecture seule » a tort laisserait croire
            // qu'on ne peut rien casser, l'erreur dangereuse.
            id={
              query.data?.read_only === true
                ? 'impersonation.banner.readOnly'
                : 'impersonation.banner.tracked'
            }
          />
        </span>
      </p>
      {remainingLabel !== null && (
        <span className={styles.remaining}>
          <FormattedMessage
            id="impersonation.banner.remaining"
            values={{ time: remainingLabel }}
          />
        </span>
      )}
      {/* Demande de Mika (26/08) : des boutons pour se déplacer pendant
          l'emprunt. Sans eux, on atterrit sur un écran qui refuse et il
          faut deviner l'adresse à taper. */}
      <a className={styles.go} href={impersonationLandingPath(role)}>
        <FormattedMessage
          id={
            emprunt.target_is_admin
              ? 'impersonation.banner.goBackoffice'
              : 'impersonation.banner.goSpace'
          }
        />
      </a>
      <button
        type="button"
        className={styles.stop}
        onClick={onStop}
        disabled={stop.isPending}
        aria-label={intl.formatMessage({ id: 'impersonation.banner.stop' })}
      >
        <FormattedMessage id="impersonation.banner.stop" />
      </button>
    </div>
  );
}
